import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ArrowRight, CheckCircle2, MessageCircle, FileDown, Zap } from "lucide-react";
import { finApi, money, apiErr } from "../api";
import { useFin } from "../FinanceApp";
import { docHtml, shareInvoiceWhatsApp } from "../lib/shareInvoice";
import { pdfInvoice } from "../lib/invoicePrint";
import { SaudiRiyal } from "../../components/SaudiRiyal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const CASH = "__cash__";
const line0 = () => ({ item_id: "", description: "", qty: 1, price: "" });
const ZSTATUS = { cleared: "معتمدة من الهيئة", reported: "مُبلّغ عنها للهيئة", rejected: "رفضتها الهيئة", error: "لم تُرسل بعد — ستُعاد المحاولة تلقائياً" };

const Steps = ({ step }) => (
  <div className="flex items-center gap-2 text-xs font-bold" data-testid="fin-quick-steps">
    {["البنود والعميل", "إصدار ومشاركة"].map((t, i) => (
      <div key={t} className={`flex flex-1 items-center gap-2 rounded-full px-3 py-1.5 ${step >= i + 1 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/25">{i + 1}</span>{t}
      </div>
    ))}
  </div>
);

const Line = ({ l, i, items, set, remove }) => (
  <div className="rounded-xl border border-slate-200 p-2.5 space-y-2" data-testid={`fin-quick-line-${i}`}>
    {items.length > 0 && (
      <Select value={l.item_id || "none"} onValueChange={(v) => { const it = items.find((x) => x.id === v); set(v === "none" ? { item_id: "" } : { item_id: v, description: it?.name || "", price: l.price || it?.sale_price || "" }); }}>
        <SelectTrigger className="h-10" data-testid={`fin-quick-item-${i}`}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-60"><SelectItem value="none">— بند حر بدون صنف —</SelectItem>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
      </Select>
    )}
    <Input placeholder="وصف البند" value={l.description} onChange={(e) => set({ description: e.target.value })} className="h-10" data-testid={`fin-quick-desc-${i}`} />
    <div className="flex items-center gap-2">
      <Input type="number" inputMode="decimal" dir="ltr" placeholder="الكمية" value={l.qty} onChange={(e) => set({ qty: e.target.value })} className="h-10 w-24" data-testid={`fin-quick-qty-${i}`} />
      <Input type="number" inputMode="decimal" dir="ltr" placeholder="السعر قبل الضريبة" value={l.price} onChange={(e) => set({ price: e.target.value })} className="h-10 flex-1" data-testid={`fin-quick-price-${i}`} />
      {remove && <button onClick={remove} className="p-2 text-slate-400 hover:text-rose-600" aria-label="حذف"><Trash2 className="h-4 w-4" /></button>}
    </div>
  </div>
);

const Done = ({ d, party, company, onClose }) => {
  const [busy, setBusy] = useState("");
  const z = d.zatca?.submission?.status;
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };
  return (
    <div className="space-y-4 text-center" data-testid="fin-quick-done">
      <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
      <div><div className="text-lg font-extrabold">تم إصدار الفاتورة {d.number}</div>
        <div className="mt-1 text-2xl font-extrabold font-mono-nums">{money(d.total)} <SaudiRiyal /></div>
        {d.status === "draft" ? <div className="mt-1 text-xs text-amber-700">محفوظة كمسودة — الترحيل يحتاج صلاحية</div>
          : z && <div className={`mt-1 text-xs font-semibold ${z === "rejected" ? "text-rose-600" : "text-emerald-700"}`} data-testid="fin-quick-zatca">{ZSTATUS[z] || z}</div>}</div>
      <Button onClick={() => run("wa", async () => { const r = await shareInvoiceWhatsApp(d, party, company); if (r === "fallback") toast.info("تم تنزيل PDF — أرفقه في محادثة واتساب"); })} disabled={!!busy}
        className="h-12 w-full gap-2 bg-[#25D366] text-base font-bold text-white hover:bg-[#1ebe5b]" data-testid="fin-quick-share-wa">
        {busy === "wa" ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageCircle className="h-5 w-5" />} مشاركة عبر واتساب
      </Button>
      <div className="flex gap-2">
        <Button variant="outline" disabled={!!busy} onClick={() => run("pdf", async () => pdfInvoice(await docHtml(d, party, company), `${d.number}.pdf`))} className="flex-1 gap-1.5" data-testid="fin-quick-pdf">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} تنزيل PDF</Button>
        <Button variant="ghost" onClick={onClose} className="flex-1" data-testid="fin-quick-close">إغلاق</Button>
      </div>
    </div>
  );
};

export function QuickInvoice({ open, onOpenChange, parties, items, vatRate, onDone }) {
  const { user, can } = useFin();
  const [step, setStep] = useState(1);
  const [partyId, setPartyId] = useState(CASH);
  const [lines, setLines] = useState([line0()]);
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);
  useEffect(() => { if (open) { setStep(1); setPartyId(CASH); setLines([line0()]); setDoc(null); } }, [open]);
  const party = parties.find((p) => p.id === partyId);
  const kind = party?.vat_number ? "standard" : "simplified";
  const valid = lines.filter((l) => (parseFloat(l.price) || 0) > 0 && (l.description || l.item_id));
  const t = useMemo(() => {
    const sub = valid.reduce((a, l) => a + (parseFloat(l.qty) || 1) * (parseFloat(l.price) || 0), 0);
    const vat = sub * vatRate / 100;
    return { sub, vat, total: sub + vat };
  }, [valid, vatRate]);
  const setLine = (i, p) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...p } : l)));
  const issue = async () => {
    setBusy(true);
    try {
      const created = await finApi.createDocument({ doc_type: "sales_invoice", date: new Date().toISOString().slice(0, 10), party_id: party ? party.id : null, invoice_kind: kind, payment_means: "10",
        lines: valid.map((l) => ({ description: l.description, qty: parseFloat(l.qty) || 1, price: parseFloat(l.price), discount: 0, tax_category: "standard", exemption_code: "", item_id: l.item_id || null })) });
      if (can("journals.post")) await finApi.postDocument(created.id);
      setDoc(await finApi.document(created.id)); setStep(3); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-md max-h-[94vh] overflow-y-auto" data-testid="fin-quick-dialog">
        <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><Zap className="h-5 w-5 text-amber-500" /> فاتورة سريعة</DialogTitle></DialogHeader>
        {step < 3 && <Steps step={step} />}
        {step === 1 && (
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>العميل</Label>
              <Select value={partyId} onValueChange={setPartyId}>
                <SelectTrigger className="h-11" data-testid="fin-quick-party"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64"><SelectItem value={CASH}>عميل نقدي (فاتورة مبسطة)</SelectItem>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">{kind === "standard" ? "فاتورة ضريبية (B2B) — للعميل رقم ضريبي" : "فاتورة ضريبية مبسطة (B2C)"}</p>
            </div>
            {lines.map((l, i) => <Line key={i} l={l} i={i} items={items} set={(p) => setLine(i, p)} remove={lines.length > 1 ? () => setLines((ls) => ls.filter((_, k) => k !== i)) : null} />)}
            <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, line0()])} className="gap-1.5" data-testid="fin-quick-add-line"><Plus className="h-4 w-4" /> بند آخر</Button>
            <Button disabled={!valid.length} onClick={() => setStep(2)} className="h-12 w-full gap-2 bg-emerald-600 text-base hover:bg-emerald-700" data-testid="fin-quick-next">
              التالي · {money(t.total)} <SaudiRiyal />
            </Button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3" data-testid="fin-quick-review">
            <div className="rounded-xl bg-slate-50 p-3 text-sm">
              <div className="mb-2 font-bold">{party?.name || "عميل نقدي"}</div>
              {valid.map((l, i) => <div key={i} className="flex justify-between py-0.5 text-slate-600"><span className="truncate">{l.description} × {l.qty || 1}</span><span className="font-mono-nums">{money((parseFloat(l.qty) || 1) * parseFloat(l.price))} <SaudiRiyal /></span></div>)}
              <div className="mt-2 border-t pt-2 text-xs text-slate-500">قبل الضريبة {money(t.sub)} <SaudiRiyal /> · الضريبة {vatRate}% {money(t.vat)} <SaudiRiyal /></div>
              <div className="text-lg font-extrabold">الإجمالي {money(t.total)} <SaudiRiyal /></div>
            </div>
            <Button disabled={busy} onClick={issue} className="h-12 w-full gap-2 bg-emerald-600 text-base hover:bg-emerald-700" data-testid="fin-quick-issue">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} إصدار الفاتورة
            </Button>
            <Button variant="ghost" onClick={() => setStep(1)} className="w-full gap-1.5" data-testid="fin-quick-back"><ArrowRight className="h-4 w-4" /> رجوع للتعديل</Button>
          </div>
        )}
        {step === 3 && doc && <Done d={doc} party={party} company={user?.company} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}
